/**
 * OtpauthQrCode — the scannable QR code of an `otpauth://` setup URI (Q-L16-98 · F-98-23, owner
 * ruling "Yes, add the QR code").
 *
 * The matrix comes from `qrcode` (`QRCode.create`, error-correction level M — the level
 * authenticator apps scan most reliably at this size) and is drawn HERE as ONE SVG path: no canvas,
 * no `data:` URL, no `innerHTML`, so it renders the same under every CSP, prints crisply, and a test
 * can read the exact modules back. The quiet zone (4 modules, the QR standard's minimum) is part of
 * the viewBox, and the colours come from the stylesheet (`.mfa-setup-qr`), dark on light in every
 * theme — an inverted code does not scan in many apps.
 *
 * The code is an image with an accessible name; the URI itself stays on the page as a link for a
 * reader on the same device (and for anyone who cannot scan), and the manual key stays beside both.
 *
 * The ONE MFA QR renderer: `<MfaSetupWizard>` draws it, and so does pos-web's own multi-method
 * setup (which used to paint a canvas through `qrcode.toCanvas`).
 *
 * 🔴 `qrcode` is LOADED ON DEMAND (`import()`), never imported statically. Statically, the encoder
 * (24.8 KB min / 9.7 KB gzip) landed in the eager shared vendor chunk that every web app's MARKETING
 * entry loads — reached through `@puffin-core/login-shell` → mfa-ui — so every public page shipped a
 * QR encoder it never runs (measured 2026-09-29 on planner-web). A dynamic import is left out of
 * every eager vendor chunk by the chunking's reachability rule, so the encoder is fetched once, by
 * the setup step that draws a code. While it loads, a placeholder of the SAME box holds the space
 * (no layout shift) and names itself busy; the manual key and the setup link beside it render at
 * once, so nobody waits on the picture. If the encoder cannot load (a deploy swapped the chunk), the
 * box says so and points at the key and the link — a failure is never a silent blank.
 *
 * @module @puffin-core/mfa-ui/OtpauthQrCode
 */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useResource } from '@puffin-core/resource-loading';

import { useMfaTranslate } from './hooks/useMfaTranslate.js';

/** The QR standard's minimum quiet zone, in modules. */
const QUIET_ZONE = 4;

let encoderPromise = null;

/**
 * The `qrcode` encoder, loaded once per page on first use.
 *
 * A REJECTED load is not cached — the next mount tries again (a transient chunk-fetch failure must not
 * poison the page for its lifetime).
 *
 * @returns {Promise<{create: Function}>}
 */
export function loadQrEncoder() {
  if (!encoderPromise) {
    // `qrcode` is CommonJS: every loader that serves it to an `import()` (Vite's pre-bundle, Rollup's
    // interop, babel-jest) wraps `module.exports` as the namespace's `default`.
    encoderPromise = import('qrcode').then(
      (mod) => mod.default,
      (err) => {
        encoderPromise = null;
        throw err;
      },
    );
  }
  return encoderPromise;
}

/**
 * The SVG path of every dark module: one `M x y h1 v1 h-1 z` square per module, offset by the quiet
 * zone.
 *
 * @param {{create: Function}} encoder - the `qrcode` module (see {@link loadQrEncoder})
 * @param {string} uri
 * @returns {{d: string, extent: number}} the path and the viewBox edge (modules + both quiet zones)
 */
export function otpauthQrPath(encoder, uri) {
  const { modules } = encoder.create(uri, { errorCorrectionLevel: 'M' });
  const squares = [];
  for (let row = 0; row < modules.size; row += 1) {
    for (let col = 0; col < modules.size; col += 1) {
      if (modules.get(row, col)) {
        squares.push(`M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`);
      }
    }
  }
  return { d: squares.join(''), extent: modules.size + QUIET_ZONE * 2 };
}

/**
 * The drawn code. Module-private — only reached once the encoder is loaded.
 *
 * @param {Object} props
 * @param {{create: Function}} props.encoder
 * @param {string} props.uri
 * @param {string} props.label
 * @returns {React.JSX.Element}
 */
function QrSvg({ encoder, uri, label }) {
  const { d, extent } = useMemo(() => otpauthQrPath(encoder, uri), [encoder, uri]);
  return (
    <svg
      className="mfa-setup-qr"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${extent} ${extent}`}
      shapeRendering="crispEdges"
      data-testid="mfa-qr-code"
    >
      <path className="mfa-setup-qr-modules" d={d} />
    </svg>
  );
}

QrSvg.propTypes = {
  encoder: PropTypes.shape({ create: PropTypes.func.isRequired }).isRequired,
  uri: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * @param {Object} props
 * @param {string} props.uri - the `otpauth://` setup URI
 * @param {string} props.label - the image's accessible name (translated by the caller)
 * @returns {React.JSX.Element}
 */
export function OtpauthQrCode({ uri, label }) {
  const str = useMfaTranslate();
  const encoder = useResource(loadQrEncoder, []);

  if (encoder.status === 'ready') {
    return <QrSvg encoder={encoder.data} uri={uri} label={label} />;
  }
  const unavailable = encoder.status === 'error';
  return (
    <div
      className="mfa-setup-qr mfa-setup-qr-pending"
      role={unavailable ? 'note' : 'status'}
      aria-busy={unavailable ? undefined : true}
      data-testid={unavailable ? 'mfa-qr-code-unavailable' : 'mfa-qr-code-loading'}
    >
      <span className={unavailable ? 'mfa-setup-qr-unavailable' : 'sr-only'}>
        {str(unavailable ? 'setup_qr_unavailable' : 'setup_qr_loading')}
      </span>
    </div>
  );
}

OtpauthQrCode.propTypes = {
  uri: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};
